import {
  showErrorToast,
  showSuccessToast,
} from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Select } from "components/Select/Select";
import { useFormik } from "formik";
import { UsuarioI } from "interfaces/Usuario";
import { useEffect, useState } from "react";
import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createUsuarioSchema } from "schemas/usuarios/createUsuarioSchema";
import { editarUsuario, postUsuario } from "services/usuarios";
import { fetchRoles } from "store/slices/appSlice";
import { AppDispatch, RootState } from "store/store";

interface Props {
  actionType: "create" | "edit" | undefined;
  onClose?: any;
  refreshTable: () => void;
  showModal: boolean;
  elemento?: UsuarioI;
}

interface initialValues {
  id?: string | number;
  nombre: string;
  apellido: string;
  email: string;
  rol_id: string | number;
}

export const UsuariosABM = ({
  showModal,
  actionType,
  onClose,
  elemento,
  refreshTable,
}: Props) => {
  const dispatch: AppDispatch = useDispatch();

  const isEditMode = actionType == "edit";
  const [isLoading, setIsLoading] = useState(false);
  const initialValues: initialValues = {
    nombre: "",
    apellido: "",
    email: "",
    rol_id: "",
  };

  const renderInitialValues = () => {
    let currentValues;

    if (isEditMode && elemento) {
      currentValues = {
        id: elemento?.id ?? "",
        nombre: elemento?.nombre ?? "",
        apellido: elemento?.apellido ?? "",
        email: elemento?.email ?? "",
        rol_id: elemento?.rol.id ?? "",
      };
    } else {
      currentValues = initialValues;
    }

    setValues(currentValues);
  };

  const handleSubmitCreate = async (params: any) => {
    try {
      setIsLoading(true);
      await postUsuario({
        nombre: params.nombre,
        apellido: params.apellido,
        email: params.email,
        rol_id: params.rol_id,
      });
      showSuccessToast("¡El usuario fue creado con éxito!");
      refreshTable();
      onClose();
    } catch (err: any) {
      showErrorToast("El usuario no pudo ser creado.");
      console.log(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitEdit = async (params: any) => {
    try {
      await editarUsuario({
        id: params.id,
        nombre: params.nombre,
        apellido: params.apellido,
        email: params.email,
        rol_id: params.rol_id,
      });
      showSuccessToast("¡El usuario fue editado con éxito!");
      refreshTable();
      onClose();
    } catch (err: any) {
      showErrorToast("El usuario no pudo ser editado.");
      console.log(err.message);
    }
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isSubmitting,
    setValues,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema: createUsuarioSchema,
    onSubmit: actionType == "create" ? handleSubmitCreate : handleSubmitEdit,
  });

  const { roles, loadingAppData } = useSelector((st: RootState) => st.appData);

  useEffect(() => {
    resetForm();
    if (actionType) {
      renderInitialValues();
    }
  }, [actionType]);

  useEffect(() => {
    if (!roles) dispatch(fetchRoles());
  }, []);

  return (
    <>
      <Modal show={showModal} onHide={onClose} size="lg" centered>
        <Modal.Header>
          <Modal.Title>
            {isEditMode ? "Editar Usuario" : "Nuevo Usuario"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <Col>
              <Row>
                <Col sm={6} className="mb-4">
                  <Input
                    id={"nombre"}
                    title="Nombre"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={6} className="mb-4">
                  <Input
                    id={"apellido"}
                    title="Apellido"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={6}>
                  <Input
                    id={"email"}
                    title="Mail @bue.edu.ar"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={6}>
                  <Select
                    id="rol_id"
                    title={"Rol"}
                    required
                    optionsFormatConfig={{ value: "id", label: "nombre" }}
                    options={roles ?? []}
                    isLoading={loadingAppData.roles}
                    onChange={handleChange}
                    placeholder="Seleccionar"
                    width="100%"
                    variant="gray-container"
                    size="lg"
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                  />
                </Col>
              </Row>
            </Col>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="mx-3"
              variant="secondary"
              size="lg"
              onClick={onClose}
            >
              Cancelar
            </Button>
            {!isLoading && (
              <Button
                variant="primary"
                size="lg"
                className="user-submit-button"
                /* disabled={isSubmitting} */ type="submit"
              >
                {isEditMode ? "Guardar Cambios" : "Confirmar"}
              </Button>
            )}

            {isLoading && (
              <div className="d-flex justify-content-center w-full">
                <Spinner />
              </div>
            )}
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};
